import { useEffect, useState } from 'react';

import { InfiniteScroll, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import WithArrow from 'components/WithArrow';
import WithResize from 'components/WithResize';
import GridText from 'components/text/GridText';

import useGridTablesResizeObserver from 'hooks/useGridTablesResizeObserver';

import useTranslation from './hooks/useTranslation';

import ClientsGridHeader from './components/ClientsGridHeader';
import ClientsGridRow from './components/ClientsGridRow';
import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const ClientGrid: FunctionComponent<Props> = ({
    className = '',
    onNext,
    rows = [],
    testId = TEST_ID
}) => {
    const { CONTACT_NAME, EMAIL, LOADING, NAME, NO_CLIENTS, NO_MORE_CLIENTS, PHONE } =
        useTranslation();

    const { containerRef, handleResize } = useGridTablesResizeObserver(
        '--clients-grid-row-columns'
    );
    const [hasNextPage, setHasNextPage] = useState<boolean>(false);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (!initialized) {
            const hasNext = typeof onNext === 'function';

            setHasNextPage(hasNext);

            if (hasNext) {
                setInitialized(true);
            }
        }
    }, [initialized, onNext]);

    const fetchNext = async () => {
        if (!hasNextPage || !onNext) {
            return;
        }

        const { done } = await onNext();

        setHasNextPage(!done);
    };

    const endingMessage = initialized && rows.length === 0 ? NO_CLIENTS : NO_MORE_CLIENTS;

    return (
        <VerticalContainer
            className={`${className} ${styles.clientsGrid}`}
            ref={containerRef}
            testId={testId}>
            <ClientsGridHeader>
                <WithResize onResize={handleResize}>
                    <WithArrow className={styles.withArrow}>
                        <GridText text={NAME} />
                    </WithArrow>
                </WithResize>

                <WithResize onResize={handleResize}>
                    <GridText text={CONTACT_NAME} />
                </WithResize>

                <WithResize onResize={handleResize}>
                    <GridText text={EMAIL} />
                </WithResize>

                <WithResize onResize={handleResize}>
                    <GridText text={PHONE} />
                </WithResize>
            </ClientsGridHeader>

            <InfiniteScroll
                endingMessage={
                    <Wrapper className={styles.wrapper}>
                        <GridText text={endingMessage} />
                    </Wrapper>
                }
                loader={
                    <Wrapper className={styles.wrapper}>
                        <GridText text={LOADING} />
                    </Wrapper>
                }
                className={styles.infiniteScroll}
                fetchNextPage={fetchNext}
                hasNextPage={hasNextPage}>
                {rows.map(({ contactName, email, id, name, phone }) => (
                    <ClientsGridRow
                        contactName={contactName}
                        email={email}
                        id={id}
                        key={id}
                        name={name}
                        phone={phone}
                    />
                ))}
            </InfiniteScroll>
        </VerticalContainer>
    );
};

export default ClientGrid;
